<template>
    <div class="stake-native">
        <!-- Stake Form -->
        <div class="mb-4">
            <label class="form-label fw-bold">Amount (SOL)</label>
            <div class="input-group mb-2">
                <input
                    type="number"
                    class="form-control form-control-lg font-mono-num"
                    v-model.number="amount"
                    :min="0.01"
                    step="0.1"
                    placeholder="0.00"
                    :disabled="loading"
                >
                <button
                    class="btn btn-outline-secondary"
                    @click="setMax"
                    :disabled="!walletConnected || loading"
                >
                    MAX
                </button>
            </div>
            <div v-if="walletConnected && solBalance !== null" class="small text-muted mb-3">
                Balance: <span class="font-mono-num">{{ formatSol(solBalance) }}</span> SOL
            </div>

            <!-- Yield Projection -->
            <div v-if="amount > 0" class="compass-info-box mb-3" style="padding: 0.75rem;">
                <div class="d-flex justify-content-between small">
                    <span>Estimated Annual Yield</span>
                    <span class="font-mono-num fw-bold pct-change pct-positive">
                        +{{ (amount * compassApy / 100).toFixed(4) }} SOL/yr
                    </span>
                </div>
                <div class="d-flex justify-content-between small text-muted">
                    <span>Per Epoch (~2.5 days)</span>
                    <span class="font-mono-num">+{{ (amount * compassApy / 100 / 146).toFixed(6) }} SOL</span>
                </div>
            </div>

            <button
                class="compass-btn compass-btn--primary w-100"
                @click="stakeSOL"
                :disabled="!canStake"
            >
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                {{ loading ? 'Staking...' : 'Stake SOL with Compass' }}
            </button>

            <div v-if="txResult" class="alert alert-success mt-3 small">
                Staked {{ txResult.amountSol }} SOL!
                <a :href="txResult.explorerUrl" target="_blank">View transaction &rarr;</a>
            </div>
            <div v-if="error" class="alert alert-danger mt-3 small">{{ error }}</div>
        </div>

        <!-- Connected: Show User's Stake Accounts -->
        <div v-if="walletConnected">
            <h4 class="mb-3">Your Stake Accounts</h4>

            <div v-if="loadingAccounts" class="text-center py-4">
                <div class="spinner-border text-primary"></div>
                <p class="small text-muted mt-2">Loading stake accounts...</p>
            </div>

            <div v-else-if="stakeAccounts.length === 0" class="text-muted small py-3">
                No stake accounts found. Stake SOL above to get started.
            </div>

            <div v-else class="table-mobile-scroll">
                <table class="compass-table">
                    <thead>
                        <tr>
                            <th>Validator</th>
                            <th class="text-end">Amount</th>
                            <th class="text-end">APY</th>
                            <th class="text-end">MEV</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="account in stakeAccounts" :key="account.address">
                            <td>
                                <div class="small">
                                    <span v-if="isCompassValidator(account.validator)" class="text-success fw-bold">
                                        <i class="fas fa-check-circle me-1"></i>Compass
                                    </span>
                                    <span v-else>
                                        <span class="font-mono">{{ validatorName(account.validator) }}</span>
                                        <span v-if="isUnderperforming(account.validator)"
                                            class="d-block text-warning small mt-1"
                                            title="This validator has lower APY than Compass"
                                        >
                                            <i class="fas fa-exclamation-triangle me-1"></i>Lower yield
                                        </span>
                                    </span>
                                </div>
                            </td>
                            <td class="text-end font-mono-num">{{ formatSol(account.solBalance) }}</td>
                            <td class="text-end font-mono-num">
                                <span v-if="isCompassValidator(account.validator)" class="pct-change pct-positive">
                                    {{ compassApy }}%
                                </span>
                                <span v-else-if="getValidatorApy(account.validator) !== null"
                                    :class="isUnderperforming(account.validator) ? 'pct-change pct-negative' : ''"
                                >
                                    {{ getValidatorApy(account.validator) }}%
                                </span>
                                <span v-else class="text-muted">—</span>
                            </td>
                            <td class="text-end font-mono-num">
                                <span v-if="account.claimableExcess > 0" class="pct-change pct-positive">
                                    +{{ formatSol(account.claimableExcess) }}
                                </span>
                                <span v-else class="text-muted">—</span>
                            </td>
                            <td>
                                <span :class="statusClass(account.status)">{{ account.status }}</span>
                            </td>
                            <td class="text-end">
                                <div class="d-flex gap-1 justify-content-end flex-wrap">
                                    <button
                                        v-if="account.claimableExcess > 0"
                                        class="btn btn-sm btn-outline-success"
                                        @click="claimMev(account)"
                                        :disabled="claiming === account.address"
                                    >
                                        {{ claiming === account.address ? '...' : 'Claim' }}
                                    </button>
                                    <button
                                        v-if="isUnderperforming(account.validator) && (account.status === 'active' || account.status === 'delegated')"
                                        class="btn btn-sm btn-primary"
                                        @click="$emit('scroll-to-optimizer')"
                                        title="Move this stake to Compass for better yield"
                                    >
                                        <i class="fas fa-arrow-up me-1"></i>Move
                                    </button>
                                    <button
                                        class="btn btn-sm btn-outline-secondary"
                                        @click="withdrawStake(account)"
                                        :disabled="withdrawing === account.address"
                                    >
                                        {{ withdrawing === account.address ? '...' : 'Withdraw' }}
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot v-if="totalClaimable > 0">
                        <tr>
                            <td colspan="2" class="fw-bold">Total Claimable MEV</td>
                            <td class="text-end font-mono-num pct-change pct-positive fw-bold">
                                +{{ formatSol(totalClaimable) }} SOL
                            </td>
                            <td colspan="2" class="text-end">
                                <button
                                    class="compass-btn compass-btn--sm compass-btn--primary"
                                    @click="claimAllMev"
                                    :disabled="claimingAll"
                                >
                                    {{ claimingAll ? 'Claiming...' : 'Claim All MEV' }}
                                </button>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>

        <!-- Not connected -->
        <div v-else class="compass-info-box mt-3">
            <p class="mb-0 small">Connect your wallet to view stake accounts, claim MEV rewards, and manage your staking positions.</p>
        </div>
    </div>
</template>

<script>
import { useSolSdk } from '../composables/useSolSdk';

export default {
    name: 'StakeNative',
    props: {
        compassVote: { type: String, required: true },
        compassApy: { type: Number, default: 7.2 },
        epochNumber: { type: Number, default: 0 },
        epochProgress: { type: Number, default: 0 },
        compasssolMint: { type: String, default: '' },
    },
    emits: ['scroll-to-optimizer'],
    data() {
        return {
            amount: null,
            loading: false,
            loadingAccounts: false,
            accountsLoaded: false,
            error: null,
            txResult: null,
            stakeAccounts: [],
            solBalance: null,
            claiming: null,
            claimingAll: false,
            withdrawing: null,
            validatorCache: {},
        };
    },
    computed: {
        walletConnected() {
            return !!window.wallet?.publicKey?.value;
        },
        canStake() {
            return this.walletConnected && this.amount > 0.01 && !this.loading;
        },
        totalClaimable() {
            return this.stakeAccounts.reduce((sum, a) => sum + (a.claimableExcess || 0), 0);
        },
    },
    watch: {
        walletConnected(connected) {
            if (connected) this.loadAccounts();
        },
    },
    mounted() {
        if (this.walletConnected) {
            this.loadAccounts();
        }
        // Listen for wallet connection changes
        this._walletInterval = setInterval(() => {
            const connected = !!window.wallet?.publicKey?.value;
            if (connected && !this.accountsLoaded && !this.loadingAccounts) {
                this.loadAccounts();
            }
        }, 2000);
    },
    beforeUnmount() {
        if (this._walletInterval) clearInterval(this._walletInterval);
    },
    methods: {
        formatSol(lamportsOrSol) {
            if (lamportsOrSol === null || lamportsOrSol === undefined) return '—';
            const val = lamportsOrSol > 1e6 ? lamportsOrSol / 1e9 : lamportsOrSol;
            return val.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
        },
        shortenAddress(addr) {
            if (!addr) return '—';
            return addr.slice(0, 4) + '...' + addr.slice(-4);
        },
        isCompassValidator(validator) {
            return validator === this.compassVote;
        },
        statusClass(status) {
            const s = (status || '').toLowerCase();
            if (s === 'active' || s === 'delegated') return 'compass-badge compass-badge--purple';
            if (s === 'deactivating') return 'compass-badge compass-badge--outline';
            if (s === 'inactive') return 'text-muted';
            return '';
        },
        setMax() {
            if (this.solBalance !== null) {
                // Leave 0.01 SOL for fees
                this.amount = Math.max(0, (this.solBalance > 1e6 ? this.solBalance / 1e9 : this.solBalance) - 0.01);
            }
        },

        validatorName(validator) {
            if (!validator) return '—';
            const data = this.validatorCache[validator];
            return data?.name || (validator.slice(0, 4) + '...' + validator.slice(-4));
        },
        getValidatorApy(validator) {
            if (!validator) return null;
            const data = this.validatorCache[validator];
            if (!data || !data.apy) return null;
            return (data.apy * 100).toFixed(2);
        },
        isUnderperforming(validator) {
            if (!validator || this.isCompassValidator(validator)) return false;
            const apy = this.getValidatorApy(validator);
            if (apy === null) return false;
            return parseFloat(apy) < this.compassApy - 0.3;
        },

        async loadValidatorData() {
            try {
                const response = await fetch('/validators/ajax');
                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        for (const v of data) {
                            this.validatorCache[v.vote_account || v.voteAccountAddress] = {
                                name: v.name,
                                apy: v.apy || v.score?.apy || 0,
                            };
                        }
                    }
                }
            } catch (e) {
                console.warn('[StakeNative] Could not load validator data:', e);
            }
        },

        async loadAccounts() {
            if (!this.walletConnected) return;
            this.loadingAccounts = true;
            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                const walletAddress = window.wallet.publicKey.value.toBase58();

                // Load validator APY data in parallel with stake accounts
                const [accounts] = await Promise.all([
                    sdk.stake.getStakeAccounts(walletAddress),
                    this.loadValidatorData(),
                ]);
                this.stakeAccounts = accounts;

                // Also get SOL balance
                const { Connection, PublicKey } = await import('@solana/web3.js');
                const connection = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                this.solBalance = await connection.getBalance(new PublicKey(walletAddress));
                this.accountsLoaded = true;
            } catch (e) {
                console.error('[StakeNative] Failed to load accounts:', e);
                this.error = 'Failed to load stake accounts. Check your connection and try again.';
            } finally {
                this.loadingAccounts = false;
            }
        },

        async stakeSOL() {
            if (!this.canStake) return;
            this.loading = true;
            this.error = null;
            this.txResult = null;

            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                const walletName = 'browser';

                const result = await sdk.stake.createAndDelegateStake(
                    walletName,
                    this.amount,
                    this.compassVote
                );

                this.txResult = result;
                this.amount = null;

                // Refresh accounts
                setTimeout(() => this.loadAccounts(), 3000);
            } catch (e) {
                this.error = e.message || 'Failed to stake SOL';
                console.error('[StakeNative] Stake failed:', e);
            } finally {
                this.loading = false;
            }
        },

        async withdrawStake(account) {
            this.withdrawing = account.address;
            this.error = null;
            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();

                await sdk.stake.withdrawStake('browser', account.address);
                setTimeout(() => this.loadAccounts(), 3000);
            } catch (e) {
                this.error = e.message || 'Failed to withdraw stake';
            } finally {
                this.withdrawing = null;
            }
        },

        async claimMev(account) {
            this.claiming = account.address;
            this.error = null;
            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                const walletAddress = window.wallet.publicKey.value.toBase58();

                await sdk.stake.claimMev('browser', walletAddress, account.address);
                setTimeout(() => this.loadAccounts(), 3000);
            } catch (e) {
                this.error = e.message || 'Failed to claim MEV';
            } finally {
                this.claiming = null;
            }
        },

        async claimAllMev() {
            this.claimingAll = true;
            this.error = null;
            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                const walletAddress = window.wallet.publicKey.value.toBase58();

                await sdk.stake.claimMev('browser', walletAddress);
                setTimeout(() => this.loadAccounts(), 3000);
            } catch (e) {
                this.error = e.message || 'Failed to claim MEV';
            } finally {
                this.claimingAll = false;
            }
        },
    },
};
</script>
